
import { Question } from "../../types/assessment";
import { createGenericOptions } from "../utils";

export const deploymentQuestions: Question[] = [
  {
    id: "dep1",
    text: "How many AI use cases have been successfully deployed?",
    dimension: "deployment",
    tooltip: "Count the number of AI applications that have moved beyond the pilot stage and are actively delivering value in production environments. Consider only implementations with measurable impact.",
    options: [
      {
        value: 0,
        label: "No AI deployments or successful implementations",
        description: "Organization has not deployed any AI applications beyond conceptual or experimental stages. No production AI systems are in operation with all initiatives remaining in planning or proof-of-concept phases."
      },
      {
        value: 1,
        label: "1-2 pilot projects in testing phase with basic functionality",
        description: "Early-stage pilot projects with rudimentary functionality. Limited to controlled testing environments with minimal integration into existing systems and no significant business process integration."
      },
      {
        value: 2,
        label: "3-5 production deployments with measured impact",
        description: "Small portfolio of operational AI applications with documented results. Systems operate in production but with limited scope, serving specific business units rather than enterprise-wide functions."
      },
      {
        value: 3,
        label: "6-10 scaled applications across multiple departments",
        description: "Moderate number of deployed AI systems operating across different business areas. Applications show stable performance with established maintenance procedures and demonstrated business value."
      },
      {
        value: 4,
        label: "11-20 integrated solutions with documented ROI",
        description: "Substantial number of AI systems deeply integrated with critical business processes. Comprehensive value tracking shows quantifiable returns with most solutions achieving expected business outcomes."
      },
      {
        value: 5,
        label: "20+ enterprise-wide AI systems with significant impact",
        description: "Extensive portfolio of mature AI systems operating across the entire organization. Applications drive substantial business transformation with strategic impact and continuous evolution based on business needs."
      }
    ]
  },
  {
    id: "dep2",
    text: "What is your AI project success rate?",
    dimension: "deployment",
    tooltip: "Calculate the percentage of AI initiatives that meet their defined success criteria within planned timelines and budgets. Consider projects over the past 1-2 years.",
    options: [
      {
        value: 0,
        label: "No successful deployments (0% success rate)",
        description: "All AI projects fail to meet their defined success criteria. Complete absence of successfully deployed AI solutions with projects consistently abandoned or indefinitely delayed."
      },
      {
        value: 1,
        label: "Limited success (<20% projects) with high failure rate",
        description: "Vast majority of AI initiatives fail to achieve objectives. Success is rare and appears unpredictable with no systematic understanding of failure patterns or success factors."
      },
      {
        value: 2,
        label: "Growing success (20-40%) with improving methodology",
        description: "Success rate below industry average but showing upward trend. Emerging patterns of success with initial lessons captured from both successes and failures to refine approach."
      },
      {
        value: 3,
        label: "Good performance (41-60%) with established processes",
        description: "Average industry success rate achieved through standardized delivery methods. Consistent approach to AI implementation with formal project management and governance controls."
      },
      {
        value: 4,
        label: "Strong track record (61-80%) with predictable outcomes",
        description: "Above-average success rate with highly reliable delivery mechanism. Well-established methodology enables accurate forecasting of project outcomes with systematic risk management."
      },
      {
        value: 5,
        label: "Industry-leading (>80%) with optimized delivery",
        description: "Exceptional success rate far exceeding industry benchmarks. Mature delivery framework continuously refined through experience, with proven ability to handle complex AI implementations across diverse contexts."
      }
    ]
  },
  {
    id: "dep3",
    text: "How do you measure AI deployment ROI?",
    dimension: "deployment",
    tooltip: "Assess the sophistication of your methods for measuring and tracking the business value and return on investment from AI implementations. Consider both financial and non-financial metrics.",
    options: [
      {
        value: 0,
        label: "No ROI measurement framework for AI projects",
        description: "Complete absence of formal value assessment for AI initiatives. No attempt to quantify costs or benefits, with investments made without expectations for measurable returns."
      },
      {
        value: 1,
        label: "Basic cost tracking with minimal benefit analysis",
        description: "Rudimentary tracking of direct project expenses with superficial benefit estimation. Assessment lacks rigor and standardization with little confidence in calculated returns."
      },
      {
        value: 2,
        label: "Standard ROI calculation with 3-4 key metrics",
        description: "Conventional financial ROI calculations using a small set of direct metrics. Focus remains primarily on tangible financial returns without capturing broader business impact."
      },
      {
        value: 3,
        label: "Multi-factor analysis with 5-7 business KPIs",
        description: "Balanced assessment framework incorporating multiple financial and operational indicators. Regular measurement against pre-implementation baselines with defined success thresholds."
      },
      {
        value: 4,
        label: "Advanced ROI framework with 8-10 impact metrics",
        description: "Comprehensive valuation methodology capturing direct, indirect, and strategic benefits. Includes quantitative and qualitative measures with long-term impact projections and scenario analysis."
      },
      {
        value: 5,
        label: "Comprehensive value assessment with predictive modeling",
        description: "Sophisticated value measurement system using advanced analytics to forecast future returns. Multi-dimensional analysis connects AI initiatives directly to enterprise value creation with continuous reassessment."
      }
    ]
  },
  {
    id: "dep4",
    text: "What is your AI deployment methodology?",
    dimension: "deployment",
    tooltip: "Evaluate the maturity of your processes for deploying AI systems into production. Consider the use of MLOps, DevOps, and other methodologies that enhance deployment efficiency and reliability.",
    options: [
      {
        value: 0,
        label: "No formal AI deployment methodology",
        description: "Completely ad hoc approach to AI implementation without documented procedures. Each deployment follows a unique path with inconsistent practices and highly variable outcomes."
      },
      {
        value: 1,
        label: "Basic waterfall approach with limited iteration",
        description: "Traditional sequential project management with minimal adaptability. Deployments occur as one-time events with extensive manual work and lengthy implementation cycles."
      },
      {
        value: 2,
        label: "Agile adoption for 30% of AI projects",
        description: "Initial application of agile techniques to a minority of AI projects. Iterative development with basic sprint planning while most projects continue using traditional approaches."
      },
      {
        value: 3,
        label: "Established MLOps for 60% of deployments",
        description: "MLOps practices applied to most AI initiatives with dedicated pipelines. Includes version control, containerization and automated testing with partially automated deployment processes."
      },
      {
        value: 4,
        label: "Advanced DevOps/MLOps (90% coverage)",
        description: "Sophisticated MLOps framework covers nearly all AI deployments. Continuous integration with automated quality gates, containerized deployment, and comprehensive monitoring systems."
      },
      {
        value: 5,
        label: "Full CI/CD pipeline with automated deployment",
        description: "End-to-end automation of the entire ML lifecycle from development to production. Self-service platforms enable rapid deployment with built-in quality controls, automated scaling, and zero-downtime updates."
      }
    ]
  },
  {
    id: "dep5",
    text: "How do you handle AI system maintenance?",
    dimension: "deployment",
    tooltip: "Assess your organization's approach to maintaining AI systems after deployment. Consider processes for monitoring model drift, retraining, and handling data changes or system dependencies.",
    options: [
      {
        value: 0,
        label: "No systematic maintenance process for AI systems",
        description: "Complete absence of planned maintenance for deployed AI systems. Systems operate until failure with no proactive monitoring, updates, or performance review mechanisms."
      },
      {
        value: 1,
        label: "Reactive maintenance with manual intervention",
        description: "Maintenance occurs exclusively in response to reported problems or system failures. All updates require direct engineer intervention with no scheduled maintenance activities."
      },
      {
        value: 2,
        label: "Monthly scheduled maintenance with basic monitoring",
        description: "Basic maintenance schedule with monthly system checks. Simple monitoring identifies major performance issues with maintenance performed according to calendar rather than system needs."
      },
      {
        value: 3,
        label: "Weekly updates with performance optimization",
        description: "Regular weekly maintenance cycle includes performance checks and optimization. Standardized processes for model evaluation and retraining based on defined performance thresholds."
      },
      {
        value: 4,
        label: "Daily monitoring with automated maintenance",
        description: "Comprehensive daily monitoring with automated alerts and remediation for common issues. Maintenance tasks follow predefined playbooks with minimal human intervention for routine activities."
      },
      {
        value: 5,
        label: "Continuous maintenance with predictive updates",
        description: "Always-on monitoring system with predictive model health assessment. Intelligent maintenance triggers updates based on early detection of performance degradation before business impact occurs."
      }
    ]
  },
  {
    id: "dep6",
    text: "What level of AI system monitoring exists?",
    dimension: "deployment",
    tooltip: "Evaluate the comprehensiveness of your monitoring for deployed AI systems. Consider tools, dashboards, and processes for tracking performance, accuracy, and operational health.",
    options: [
      {
        value: 0,
        label: "No monitoring of AI systems in production",
        description: "Complete absence of monitoring for deployed AI systems. Performance issues remain undiscovered until reported by users or manifest as complete system failures."
      },
      {
        value: 1,
        label: "Basic logging with manual checks (85% uptime)",
        description: "Rudimentary logging captures system events with occasional manual review. Minimal uptime requirements with slow detection and response to operational issues."
      },
      {
        value: 2,
        label: "Automated alerts with 90% system uptime",
        description: "Simple monitoring tools generate automated notifications when predefined thresholds are breached. Reasonable uptime with standardized incident response protocols."
      },
      {
        value: 3,
        label: "Real-time monitoring with 95% uptime",
        description: "Active monitoring dashboards provide real-time visibility into system performance. Strong reliability with defined escalation paths and service level agreements."
      },
      {
        value: 4,
        label: "Advanced monitoring with 99% uptime guarantee",
        description: "Sophisticated monitoring solution covering model behavior, data quality, and infrastructure health. High-reliability operations with redundancy and rapid failover capabilities."
      },
      {
        value: 5,
        label: "Predictive monitoring with 99.9%+ availability",
        description: "Comprehensive monitoring platform uses AI to anticipate potential issues before they impact performance. Enterprise-grade reliability with automatic remediation and cross-system dependency awareness."
      }
    ]
  },
  {
    id: "dep7",
    text: "How do you manage AI deployment risks?",
    dimension: "deployment",
    tooltip: "Assess your organization's approach to identifying, evaluating, and mitigating risks associated with AI deployments. Consider technical, operational, ethical, and business risks.",
    options: [
      {
        value: 0,
        label: "No formal risk management for AI deployments",
        description: "Complete absence of structured risk assessment or management. AI systems deployed without consideration of potential risks or mitigation strategies."
      },
      {
        value: 1,
        label: "Basic risk checklist with quarterly review",
        description: "Rudimentary risk identification using simple checklists or templates. Infrequent reviews focus primarily on obvious technical risks with minimal mitigation planning."
      },
      {
        value: 2,
        label: "Monthly risk assessment with mitigation plans",
        description: "Regular risk reviews consider both technical and basic operational factors. Standard mitigation plans exist for commonly identified risks with assigned ownership."
      },
      {
        value: 3,
        label: "Weekly risk monitoring with active management",
        description: "Comprehensive risk framework addresses technical, operational, ethical, and compliance dimensions. Ongoing monitoring with established escalation procedures and contingency planning."
      },
      {
        value: 4,
        label: "Daily risk tracking with automated alerts",
        description: "Advanced risk management platform continuously evaluates deployment risks across multiple categories. Near real-time risk indicators with proactive mitigation and formal governance oversight."
      },
      {
        value: 5,
        label: "Real-time risk assessment with ML-driven mitigation",
        description: "Sophisticated risk intelligence system uses AI to dynamically identify emerging risks and recommend mitigation strategies. Integrated with deployment processes to prevent high-risk deployments."
      }
    ]
  },
  {
    id: "dep8",
    text: "What is your AI testing and validation process?",
    dimension: "deployment",
    tooltip: "Evaluate the thoroughness of your testing processes for AI systems before and during production. Consider aspects like data validation, model validation, A/B testing, and performance testing.",
    options: [
      {
        value: 0,
        label: "No formal testing process for AI systems",
        description: "Complete absence of structured testing methodology for AI systems. Deployments proceed without validated performance or accuracy metrics against defined requirements."
      },
      {
        value: 1,
        label: "Basic testing with 50% manual validation",
        description: "Rudimentary testing focused on core functionality only. Heavy reliance on manual checks with limited coverage of possible scenarios or data variations."
      },
      {
        value: 2,
        label: "Standard QA with 70% automated testing",
        description: "Conventional testing approach with growing automation for repetitive checks. Covers primary functionality but lacks comprehensive validation of edge cases and real-world scenarios."
      },
      {
        value: 3,
        label: "Comprehensive testing with 85% coverage",
        description: "Well-established testing methodology includes automated test suites and performance evaluation. Implements A/B testing for key features with appropriate statistical validation."
      },
      {
        value: 4,
        label: "Advanced validation with 95% test automation",
        description: "Sophisticated testing framework covers functional, performance, security, and fairness dimensions. Near-complete automation with robust validation of model behavior across diverse conditions."
      },
      {
        value: 5,
        label: "Full automation with continuous validation",
        description: "End-to-end testing ecosystem automatically validates all aspects of AI systems throughout their lifecycle. Includes adversarial testing, synthetic data generation, and autonomous verification of ethical compliance."
      }
    ]
  },
  {
    id: "dep9",
    text: "How do you handle AI system updates and versions?",
    dimension: "deployment",
    tooltip: "Assess your organization's processes for managing updates, versions, and releases of AI systems. Consider version control, canary releases, rollback capabilities, and documentation practices.",
    options: [
      {
        value: 0,
        label: "No version control or update management",
        description: "Complete absence of version management for AI systems. Updates occur without tracking, documentation, or rollback capability, creating high operational risk."
      },
      {
        value: 1,
        label: "Basic versioning with manual releases",
        description: "Rudimentary version tracking with entirely manual update processes. Minimal documentation of changes with high-touch deployment requiring system downtime."
      },
      {
        value: 2,
        label: "Standard Git workflow with weekly updates",
        description: "Basic version control using standard tools with regular scheduled releases. Manual approval and implementation process with limited ability to rollback problematic changes."
      },
      {
        value: 3,
        label: "CI/CD pipeline with automated deployment",
        description: "Modern CI/CD approach automates build, testing, and deployment steps. Includes comprehensive version control, systematic validation, and documented rollback procedures."
      },
      {
        value: 4,
        label: "Advanced release management with rollback",
        description: "Sophisticated release management includes canary deployments, feature flags, and one-click rollback. Comprehensive change documentation with automated validation at each stage."
      },
      {
        value: 5,
        label: "Continuous deployment with zero-downtime",
        description: "State-of-the-art version management enables seamless updates without service interruption. Includes automated canary analysis, progressive traffic shifting, and instantaneous rollback capabilities."
      }
    ]
  },
  {
    id: "dep10",
    text: "Do you benchmark your AI projects against competitors?",
    dimension: "deployment",
    tooltip: "Evaluate how systematically your organization compares its AI implementations against industry standards, best practices, and competitor capabilities. Consider both formal benchmarking and informal assessments.",
    options: [
      {
        value: 0,
        label: "No competitive benchmarking process",
        description: "Complete absence of comparison against external reference points. AI capabilities developed in isolation without consideration of industry standards or competitive positioning."
      },
      {
        value: 1,
        label: "Basic annual comparison with 2-3 metrics",
        description: "Rudimentary benchmarking conducted yearly using minimal metrics. Relies solely on publicly available information with superficial analysis and limited actionable insights."
      },
      {
        value: 2,
        label: "Quarterly analysis with 4-6 industry KPIs",
        description: "Regular comparison using standard industry indicators on a quarterly basis. Focus on major competitors with moderate depth of analysis and some strategic implications."
      },
      {
        value: 3,
        label: "Monthly benchmarking with 7-10 metrics",
        description: "Comprehensive monthly assessment across multiple performance dimensions. Combines market research, industry reports, and targeted competitive analysis with action planning."
      },
      {
        value: 4,
        label: "Weekly performance review with custom metrics",
        description: "Sophisticated benchmarking program uses tailored metrics relevant to specific business context. Deep competitive intelligence with direct performance comparisons and detailed gap analysis."
      },
      {
        value: 5,
        label: "Continuous benchmarking with industry standards",
        description: "Real-time comparative assessment against best-in-class implementations and emerging capabilities. Combines multiple data sources with advanced analytics to continuously position initiatives."
      }
    ]
  }
];
